/**
 * Renderer-side client for the Proxy RT JIT playback service that lives in
 * the Electron main process (proxyRtService.js).
 *
 * startProxyRt() resolves as soon as the local HLS playlist is servable —
 * usually well under a second — and returns the playback URL. Segments are
 * encoded just-in-time by the main process, so the player can start and seek
 * immediately while FFmpeg fills in the rest in the background.
 */

let progressHandler = null;
let listenerBound = false;

function bindProgressListener() {
    if (listenerBound || !window.ipcRenderer) {
        return;
    }
    listenerBound = true;
    window.ipcRenderer.on("proxy-rt-progress", (event, progress) => {
        try {
            progressHandler?.(progress);
        } catch (err) {
            console.error("Proxy RT progress handler error:", err);
        }
    });
}

/**
 * Start (or resume from cache) a Proxy RT session.
 *
 * @param {object} opts
 * @param {string} opts.inputPath - absolute path of the source media
 * @param {number} [opts.duration] - duration hint in seconds (from probe)
 * @param {string} [opts.quality] - "draft" (480p, default) or "review" (720p)
 * @param {(progress: object) => void} [opts.onProgress]
 * @returns {Promise<{url:string, sessionId:string, cached:boolean,
 *          complete:boolean, duration:number, totalSegments:number}>}
 */
export async function startProxyRt({ inputPath, duration, quality, onProgress }) {
    if (!window.ipcRenderer) {
        throw new Error("Proxy RT is only available in the desktop application");
    }
    bindProgressListener();
    progressHandler = onProgress || null;

    const result = await window.ipcRenderer.invoke("proxy-rt-start", {
        inputPath,
        duration,
        quality,
    });

    if (!result || result.error) {
        progressHandler = null;
        throw new Error(result?.error || "Proxy RT failed to start");
    }

    return result;
}

/** Stop the active Proxy RT session (kills FFmpeg, keeps the disk cache). */
export async function stopProxyRt() {
    progressHandler = null;
    if (!window.ipcRenderer) {
        return;
    }
    try {
        await window.ipcRenderer.invoke("proxy-rt-stop");
    } catch (err) {
        console.error("Proxy RT stop failed:", err);
    }
}
